import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const mediaQuotes = [
    '厚生労働省の統計によると、年間15万件以上の墓じまいが行われています',
    'NHK「あさイチ」でも特集された“墓じまい”──家族の想いを大切にする選択です',
    '石田純一さんも語る“子どもに負担をかけない終活”──墓じまいはその一歩',
    '岸惠子さんも語る“自分の最期を自分で決める”──墓じまいという選択',
  ];

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1734359180557-51f6e6ea10cc?q=80&w=1080"
          alt="新潟・長野の穏やかな山並みと墓地の風景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-24 sm:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-block pt-8"
          >
            <span className="inline-flex items-center px-12 py-4 rounded-full bg-amber-600/90 text-white text-xl md:text-2xl font-bold backdrop-blur-sm shadow-lg">
              もう悩まない！
            </span>
          </motion.div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
            新潟・長野の墓じまい相談窓口
            <span className="block mt-4 text-2xl sm:text-3xl lg:text-4xl font-normal text-stone-200">
              信頼と実績の池田石材興業が<br />
              「墓じまい安心相談センター」で徹底サポート！
            </span>
          </h1>

          {/* Media Quotes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            aria-label="メディア引用"
            className="max-w-4xl mx-auto"
          >
            <div className="relative text-left">
              {/* Layered gradient backdrop */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/15 via-stone-100/10 to-amber-50/15 backdrop-blur-xl rounded-2xl" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.1),transparent_50%)] rounded-2xl" />
              <div className="absolute inset-0 border border-white/30 rounded-2xl" />

              <div className="relative px-6 py-4 md:px-10 md:py-6">
                <ul className="space-y-3 text-center">
                  {mediaQuotes.map((q, i) => (
                    <li key={i}>
                      <span className="text-stone-200 text-lg leading-relaxed">
                        「{q}」
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4"
          >
            <button
              onClick={scrollToContact}
              className="w-full sm:w-auto px-10 py-4 bg-amber-600 hover:bg-amber-700 text-white text-lg font-bold rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              無料で相談
            </button>
            <button
              onClick={scrollToContact}
              className="w-full sm:w-auto px-10 py-4 bg-green-600 hover:bg-green-700 text-white text-lg font-bold rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center justify-center gap-2"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              LINEで相談
            </button>
          </motion.div>

          {/* Trust Message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="text-stone-300 text-base max-w-2xl mx-auto pb-12"
          >
            まずは、お気軽にメールやLINEからご相談ください。
            <br />
            一緒に、これからの一番よい形を考えてまいります。
          </motion.p>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <ChevronDown className="w-8 h-8 text-white animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
